import React from 'react';
import { ExternalLink, ArrowRight, Cpu, Zap, BarChart3, Briefcase, Truck } from 'lucide-react';
import { motion } from 'framer-motion';
import { veenaPlatform } from '../../data/mock';
import { useLanguage } from '../../context/LanguageContext';

/**
 * Featured platform section for Smartinvest subsidiary — showcases veena.
 * Quantum computing made accessible.
 */
const VeenaSection = () => {
  const { pick } = useLanguage();
  const orange = veenaPlatform.brandColor;
  const domainIcons = [Cpu, Zap, BarChart3, Briefcase, Truck];

  return (
    <section
      className="section-padding-lg border-t relative overflow-hidden"
      style={{
        background: `linear-gradient(180deg, ${veenaPlatform.bgDark} 0%, #050a14 100%)`,
        borderColor: `${orange}40`
      }}
    >
      {/* Ambient orange glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full blur-3xl opacity-30"
          style={{ background: `radial-gradient(circle, ${orange}50 0%, transparent 70%)` }}
        />
        <div
          className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full blur-3xl opacity-25"
          style={{ background: `radial-gradient(circle, ${orange}40 0%, transparent 70%)` }}
        />
        {/* Subtle quantum grid */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `linear-gradient(${orange} 1px, transparent 1px), linear-gradient(90deg, ${orange} 1px, transparent 1px)`,
            backgroundSize: '80px 80px'
          }}
        />
      </div>

      <div className="container-swg relative z-10">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span
            className="inline-block px-4 py-1.5 text-[11px] tracking-[0.25em] uppercase font-medium"
            style={{ color: orange, border: `1px solid ${orange}`, background: `${orange}10` }}
            data-testid="veena-eyebrow"
          >
            {pick(veenaPlatform.eyebrow)}
          </span>

          <div className="mt-8 flex items-center justify-center gap-5 flex-wrap">
            <img
              src={veenaPlatform.logo}
              alt="veena"
              className="h-14 w-14 object-contain drop-shadow-[0_0_20px_rgba(255,107,0,0.5)]"
              data-testid="veena-logo"
            />
            <h2 className="text-5xl md:text-6xl text-white" style={{ fontFamily: 'serif', fontWeight: 500 }}>
              veena
            </h2>
          </div>
          <p className="text-3xl md:text-4xl mt-4 italic" style={{ color: orange, fontFamily: 'serif' }}>
            {pick(veenaPlatform.tagline)}
          </p>
          <p className="text-[#a8a39a] mt-3 text-base tracking-wider">
            {pick(veenaPlatform.subtagline)}
          </p>
        </motion.div>

        {/* Preview image + brief */}
        <div className="grid lg:grid-cols-2 gap-10 items-center mb-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <a
              href="#" onClick={(e) => e.preventDefault()} aria-disabled="true"
              className="block relative group rounded-none overflow-hidden border shadow-[0_20px_60px_-20px_rgba(255,107,0,0.5)]"
              style={{ borderColor: orange }}
              data-testid="veena-preview-link"
            >
              <img
                src={veenaPlatform.preview}
                alt="veena — Quantum is here"
                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                loading="lazy"
              />
              <div
                className="absolute bottom-4 right-4 px-4 py-2 backdrop-blur-md text-xs font-medium flex items-center gap-2 opacity-90 group-hover:opacity-100 transition-opacity tracking-widest uppercase"
                style={{ background: `${orange}25`, border: `1px solid ${orange}`, color: orange }}
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Live
              </div>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <p className="text-lg text-white leading-relaxed mb-5">{pick(veenaPlatform.shortBrief)}</p>
            <p className="text-[#a8a39a] leading-relaxed mb-8">{pick(veenaPlatform.longBrief)}</p>

            {/* Algorithms badges */}
            <div className="mb-8">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#a8a39a] mb-3">Powered by</p>
              <div className="flex flex-wrap gap-2">
                {veenaPlatform.algorithms.map((algo) => (
                  <span
                    key={algo}
                    className="px-3 py-1.5 text-xs font-mono tracking-wider"
                    style={{ color: orange, border: `1px solid ${orange}80`, background: `${orange}10` }}
                  >
                    {algo}
                  </span>
                ))}
              </div>
            </div>

            <a
              href="#" onClick={(e) => e.preventDefault()} aria-disabled="true"
              className="inline-flex items-center gap-3 px-10 py-4 font-semibold text-sm tracking-[0.2em] uppercase transition-all duration-300 hover:scale-[1.03]"
              style={{
                background: 'transparent',
                color: orange,
                border: `1px solid ${orange}`,
                boxShadow: `0 8px 30px ${orange}30`
              }}
              data-testid="veena-cta"
            >
              {pick(veenaPlatform.cta)}
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>

        {/* Domains grid */}
        <div className="mb-12">
          <h3 className="text-center text-[#a8a39a] uppercase tracking-[0.3em] text-xs mb-8 font-mono">
            {pick({ en: 'Application Domains', fr: "Domaines d'application" })}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {veenaPlatform.domains.map((d, index) => {
              const Icon = domainIcons[index];
              return (
                <motion.div
                  key={d.name.en}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.06 }}
                  className="p-5 flex flex-col items-center text-center gap-3"
                  style={{ background: `${orange}08`, border: `1px solid ${orange}30` }}
                  data-testid={`veena-domain-${index}`}
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: `${orange}20`, border: `1px solid ${orange}50` }}
                  >
                    <Icon className="w-4 h-4" style={{ color: orange }} />
                  </div>
                  <span className="text-white text-sm font-medium">{pick(d.name)}</span>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Pricing strip */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto p-6 text-center"
          style={{ background: `${orange}10`, border: `1px solid ${orange}40` }}
          data-testid="veena-pricing"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#a8a39a] mb-3">Pricing</p>
          <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
            {pick(veenaPlatform.pricing).map((p, i) => (
              <React.Fragment key={p}>
                {i > 0 && <span style={{ color: orange }}>·</span>}
                <span className="text-white font-medium">{p}</span>
              </React.Fragment>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default VeenaSection;
